"use client"

// ds-typo-ignore-file — phone mockups simulate the get.aheadhealth.com quiz UI (intentional sub-DS sizes), not marketing-site typography
import { useEffect, useRef, useState, type CSSProperties } from "react"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { QuizCtaLink } from "@/components/quiz/quiz-cta-link"
import { blobMediaUrl } from "@/lib/blob-url"
import { useLocalePath } from "@/lib/use-localized-navigation"
import styles from "./quiz-cta-banner-membership-first.module.css"

const BANNER_IMAGE = blobMediaUrl(
  "/images/marketing/quiz-banner-wood-two-phones-v5.webp"
)

/**
 * The quiz banner on the membership-first site: photographed phones with
 * locale-specific screen overlays, a filled CTA and a contact button.
 * `QuizCtaBanner` renders it in place of the live banner when the flag is on
 * (after its own country gate); at launch it replaces that banner.
 */
export function MembershipFirstQuizCtaBanner({
  variant = "default",
}: {
  variant?: "default" | "compact"
}) {
  const compact = variant === "compact"
  const t = useTranslations("ui")
  const lp = useLocalePath()
  const ctaLabel = t("quizCtaCta")

  return (
    <section
      data-testid="quiz-cta-banner"
      className={`${styles.banner} ${compact ? "" : styles.full}`}
      style={{ "--banner-image": `url("${BANNER_IMAGE}")` } as CSSProperties}
    >
      <div className={styles.shade} aria-hidden="true" />
      <div className={styles.copy}>
        {/* Medium (Eugene, 01.10: "thicker"). */}
        <h2 className={`type-display-sm-emphasized ${styles.headline}`}>
          {t("quizCtaHeadline").replaceAll("-", "\u2011")}
        </h2>
        {!compact && (
          <p className={`type-body-xl ${styles.subtext}`}>
            {t("quizCtaSubtext")}
          </p>
        )}
        <div className={styles.actions}>
          <QuizCtaLink
            className={cn(
              buttonVariants({ variant: "primary" }),
              "bg-neutral-950 hover:bg-neutral-900 focus-visible:ring-neutral-400",
              // German's "Den richtigen Ausgangspunkt finden" outgrows the
              // pill on a phone.
              "max-sm:h-auto max-sm:min-h-12 max-sm:w-full max-sm:py-3 max-sm:text-center max-sm:whitespace-normal"
            )}
          >
            {ctaLabel}
          </QuizCtaLink>
          <Link
            href={lp("/contact")}
            className={cn(buttonVariants({ variant: "tonal" }), styles.contact)}
          >
            {t("contactUs")}
          </Link>
        </div>
      </div>
      <div className={styles.visual}>
        {/* The photo and the screens on its phones move as one, at the
            photo's proportions, so the screens stay on the phones while the
            photo covers the banner at any width. */}
        <div className={styles.stage}>
          <Image
            src={BANNER_IMAGE}
            alt=""
            width={2163}
            height={727}
            quality={90}
            sizes="(max-width: 600px) 270vw, (max-width: 900px) 140vw, 100vw"
            className={styles.scene}
          />
          <div className={styles.grain} aria-hidden="true" />
          <QuizCtaLink aria-label={ctaLabel} className={styles.phones}>
            <QuizScreenOverlay
              screen="question"
              className={styles.firstScreen}
            />
            <QuizScreenOverlay screen="about" className={styles.secondScreen} />
          </QuizCtaLink>
        </div>
      </div>
    </section>
  )
}

/** Fit the translated UI to the photographed screen, including on resize. */
function QuizScreenOverlay({
  screen,
  className,
}: {
  screen: "question" | "about"
  className: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const slot = ref.current
    if (!slot) return
    const fit = () => setScale(slot.clientWidth / 208)
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(slot)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`${styles.screenSlot} ${className}`}
    >
      <QuizPhone screen={screen} scale={scale} />
    </div>
  )
}

/** Stylised Ahead arrow — from `public/icons/ahead-logo.svg`. */
function AheadArrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 15 16"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0H11.3742Z" />
      <path d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z" />
    </svg>
  )
}

/**
 * Localized decorative quiz screen fitted inside a photographed phone.
 *   - "question": step 1 — "What would you like to focus on?"
 *   - "about": step 2 — "A bit about you" (age + gender).
 */
function QuizPhone({
  screen = "question",
  scale = 1,
}: {
  screen?: "question" | "about"
  scale?: number
}) {
  const step = screen === "question" ? 1 : 2
  const t = useTranslations("ui")
  const locale = useLocale()
  return (
    <div
      className="relative h-[408px] w-[208px] overflow-hidden bg-white"
      style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}
    >
      <div className="flex flex-col items-center px-4 pt-7 pb-3">
        <AheadArrow className="text-aubergine-800 h-3.5 w-3.5" />
        <div className="mt-2 flex w-full items-center justify-between">
          <span className="type-label-sm text-neutral-500">
            {t("quizPhoneStep", { step: String(step) })}
          </span>
          {/* The badge mimics the quiz's own language chip; it has to follow
            the language the mockup is now drawn in. */}
          <span className="type-label-sm text-neutral-400">
            {locale.toUpperCase()}
          </span>
        </div>
        <div className="mt-2 flex w-full gap-1">
          <span className="bg-aubergine-800 h-1 flex-1 rounded-full" />
          <span
            className={
              "h-1 flex-1 rounded-full " +
              (step >= 2 ? "bg-aubergine-800" : "bg-neutral-200")
            }
          />
          <span className="h-1 flex-1 rounded-full bg-neutral-200" />
        </div>
      </div>
      {screen === "question" ? <QuestionScreen /> : <AboutScreen />}
    </div>
  )
}

function QuestionScreen() {
  const t = useTranslations("ui")
  const options = [
    { id: "general", label: t("quizPhoneFocusGeneral"), checked: true },
    { id: "heart", label: t("quizPhoneFocusHeart"), checked: false },
    { id: "hormones", label: t("quizPhoneFocusHormones"), checked: false },
    { id: "brain", label: t("quizPhoneFocusBrain"), checked: false },
  ]
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3
        className="mt-3 text-center text-[13px] font-medium text-neutral-900"
        // ds-typo-ignore: inline phone-screen mockup — not page typography
      >
        {t("quizPhoneQuestionTitle")}
      </h3>
      <p
        className="mt-0.5 text-center text-[10px] text-neutral-400"
        // ds-typo-ignore
      >
        {t("quizPhoneSelectAll")}
      </p>
      <ul className="mt-3 flex flex-col gap-1.5">
        {options.map((o) => (
          <li
            key={o.id}
            className="flex items-center gap-2 rounded-[10px] bg-neutral-50 px-2.5 py-2"
          >
            <span
              className={
                "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border " +
                (o.checked
                  ? "border-aubergine-800 bg-aubergine-800 text-white"
                  : "border-neutral-300 bg-white")
              }
            >
              {o.checked && (
                <svg
                  viewBox="0 0 10 10"
                  className="h-2 w-2"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 5l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span
              className="text-[10px] font-medium text-neutral-900"
              // ds-typo-ignore
            >
              {o.label}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-auto flex justify-end pt-2">
        <span
          className="bg-aubergine-800 rounded-full px-3 py-1.5 text-[10px] font-medium text-white"
          // ds-typo-ignore
        >
          {t("quizPhoneContinue")}
        </span>
      </div>
    </div>
  )
}

/** Step 2 of the live quiz — "A bit about you" (age range + gender). */
function AboutScreen() {
  const t = useTranslations("ui")
  const ageRanges = [
    { id: "under40", label: t("quizPhoneAgeUnder40"), on: false },
    { id: "40to59", label: t("quizPhoneAge40to59"), on: true },
    { id: "60to69", label: t("quizPhoneAge60to69"), on: false },
    { id: "70plus", label: t("quizPhoneAge70plus"), on: false },
  ]
  const genders = [
    { id: "male", label: t("quizPhoneMale"), on: true },
    { id: "female", label: t("quizPhoneFemale"), on: false },
  ]
  const pill = (on: boolean) =>
    "rounded-[10px] border px-2 py-2 text-center text-[10px] font-medium " +
    (on
      ? "border-aubergine-800 bg-aubergine-50 text-aubergine-900"
      : "border-neutral-200 bg-neutral-50 text-neutral-700")
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3
        className="mt-3 text-center text-[13px] font-medium text-neutral-900"
        // ds-typo-ignore: inline phone-screen mockup — not page typography
      >
        {t("quizPhoneAboutTitle")}
      </h3>
      <p
        className="mt-3 text-[10px] font-medium text-neutral-500"
        // ds-typo-ignore
      >
        {t("quizPhoneAgeRange")}
      </p>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {ageRanges.map((o) => (
          <span key={o.id} className={pill(o.on)}>
            {o.label}
          </span>
        ))}
      </div>
      <p
        className="mt-3 text-[10px] font-medium text-neutral-500"
        // ds-typo-ignore
      >
        {t("quizPhoneGenderAtBirth")}
      </p>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {genders.map((o) => (
          <span key={o.id} className={pill(o.on)}>
            {o.label}
          </span>
        ))}
      </div>
      <div className="mt-auto flex justify-end pt-2">
        <span
          className="bg-aubergine-800 rounded-full px-3 py-1.5 text-[10px] font-medium text-white"
          // ds-typo-ignore
        >
          {t("quizPhoneContinue")}
        </span>
      </div>
    </div>
  )
}
